<!-- Ported from the Polaris kit: src/product/components/MessageBubble.vue.
     Classes translated to Tailwind 3 by rebranding_frontend/port-kit-component.mjs;
     strings moved to i18n by hand. Change the design in the kit first.
     Hand changes (phase 4.6): the Polaris symbol in the header is the app's PolarisMark (the kit reads
     ./brand/symbol-ink.svg); the accessible names ("Message from…", "Answer from…") go through i18n. -->
<script setup>
// One turn in a conversation, on the registry message and bubble. The customer speaks on a grey bubble next to their initials; Polaris AI
// answers on its gradient (the brand's, or the one of its role), identified by the Polaris symbol and its name as well as by color; a
// human agent is carbon, a private note amber. The component has no outer margin: the page decides the rhythm and the indent.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import PolarisMark from 'dashboard/components-next/sidebar/PolarisMark.vue';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Bubble, BubbleContent } from '../ui/bubble';
import {
  Message,
  MessageAvatar,
  MessageContent,
  MessageFooter,
  MessageHeader,
} from '../ui/message';
import { initials } from './initials';

const props = defineProps({
  // customer | agent (Polaris AI) | human | note
  from: {
    type: String,
    required: true,
    validator: value => ['customer', 'agent', 'human', 'note'].includes(value),
  },
  author: { type: String, required: true },
  // the role of the AI: service | sales | ecommerce | general | receptionist
  role: { type: String, default: '' },
  meta: { type: String, default: '' },
});
const VARIANTS = {
  customer: 'customer',
  agent: 'ai',
  human: 'agent',
  note: 'note',
};
const ROLES = {
  service: 'ai-service',
  sales: 'ai-sales',
  ecommerce: 'ai-ecommerce',
  general: 'ai-general',
  receptionist: 'ai-receptionist',
};
const { t } = useI18n();
const variant = computed(
  () => (props.from === 'agent' && ROLES[props.role]) || VARIANTS[props.from]
);
const label = computed(() => {
  if (props.from === 'customer') {
    return t('POLARIS.PAGES.MESSAGE_BUBBLE.FROM', { author: props.author });
  }
  if (props.from === 'note') {
    return t('POLARIS.PAGES.MESSAGE_BUBBLE.NOTE', { author: props.author });
  }
  return t('POLARIS.PAGES.MESSAGE_BUBBLE.ANSWER', { author: props.author });
});
</script>

<template>
  <Message
    as="article"
    class="w-auto"
    :align="from === 'customer' ? 'start' : 'end'"
    :aria-label="label"
  >
    <MessageAvatar v-if="from === 'customer'">
      <Avatar>
        <AvatarFallback class="type-polaris-caption font-medium">
          {{ initials(author) }}
        </AvatarFallback>
      </Avatar>
    </MessageAvatar>
    <MessageContent
      class="min-w-0 flex-1"
      :class="from === 'customer' ? '' : 'items-end'"
    >
      <MessageHeader
        v-if="from !== 'customer'"
        class="flex items-center gap-1.5"
      >
        <PolarisMark v-if="from === 'agent'" size="xs" />
        {{ author }}
      </MessageHeader>
      <Bubble
        :variant="variant"
        :align="from === 'customer' ? 'start' : 'end'"
        class="max-w-[92%]"
      >
        <BubbleContent class="grid gap-2 px-4 py-3 leading-relaxed">
          <slot />
        </BubbleContent>
      </Bubble>
      <div v-if="$slots.footer" class="flex flex-wrap items-center gap-2">
        <slot name="footer" />
      </div>
      <MessageFooter v-if="meta">{{ meta }}</MessageFooter>
    </MessageContent>
  </Message>
</template>
